import { Component, OnInit, ViewChild } from "@angular/core";
import { BulkEditTechnicalLettersService } from "../../services/technical-letters/bulkedit-technicalletters.service";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import {
  BulkEditTechnicalLetter,
  LookUpData,
} from "../../interfaces/technical-letters/bulk-edit-technical-letter.models";
import * as ExcelJS from "exceljs";

@Component({
    selector: "app-bulk-edit",
    templateUrl: "./bulk-edit.component.html",
    styleUrls: ["./bulk-edit.component.css"],
    providers: [BulkEditTechnicalLettersService],
    standalone: false
})
export class BulkEditComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  dataSource: MatTableDataSource<BulkEditTechnicalLetter> =
    new MatTableDataSource();
  bulkEditData: BulkEditTechnicalLetter[] = [];
  lookupData: LookUpData[] = [];
  modifiedLookupData: { leId: number; lookupValue: string }[] = [];
  filters: { [key: string]: string } = {};
  error: string | null = null;
  editingRowIndex: number | null = null;
  editingRows: { [key: number]: boolean } = {};
  editCache: { [key: number]: Partial<BulkEditTechnicalLetter> } = {};
  showAllRecords: boolean = false;
  isLoading: boolean = false;

  columns = [
    {
      key: "letterEquipId",
      label: "Letter Equip ID",
      filterable: true,
      sortable: true,
    },
    { key: "letterIssuer", label: "Issuer", filterable: true, sortable: true },
    {
      key: "letterNumber",
      label: "Letter Number",
      filterable: true,
      sortable: true,
    },
    {
      key: "letterTitle",
      label: "Letter Title",
      filterable: true,
      sortable: true,
    },
    {
      key: "recurringFlag",
      label: "Recurring",
      filterable: true,
      sortable: true,
    },
    {
      key: "letterIssueDate",
      label: "Issue Date",
      filterable: true,
      sortable: true,
    },
    { key: "plant", label: "Plant", filterable: true, sortable: true },
    {
      key: "equipment",
      label: "Equipment #",
      filterable: true,
      sortable: true,
    },
    {
      key: "equipmentType",
      label: "Equipment Type",
      filterable: true,
      sortable: true,
    },
    {
      key: "disposition",
      label: "Disposition",
      filterable: true,
      sortable: true,
    },
    { key: "comments", label: "Comments", filterable: true, sortable: true },
    { key: "actions", label: "Actions", filterable: false, sortable: false },
  ];

  columnKeys = this.columns.map((col) => col.key);

  constructor(
    private bulkEditTechnicalLettersService: BulkEditTechnicalLettersService
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this.bulkEditTechnicalLettersService
      .getBulkEditTechnicalLettersDataAsync()
      .subscribe(
        (data) => {
          this.bulkEditData = data;
          this.dataSource.data = data;

          this.dataSource.filterPredicate = (row, filter: string) => {
            const filters = JSON.parse(filter);
            return this.columns.every((col) => {
              if (!col.filterable) return true;
              const filterValue = (filters[col.key] || "").toLowerCase();
              const rowValue = (
                row[col.key as keyof BulkEditTechnicalLetter] || ""
              )
                .toString()
                .toLowerCase();
              return !filterValue || rowValue.includes(filterValue);
            });
          };
          this.isLoading = false;
        },
        (error) => {
          console.error(
            "Error fetching bulk edit service bulletins data:",
            error
          );
        }
      );
  }

  applyFilters() {
    this.dataSource.filter = JSON.stringify(this.filters);
  }

  onFilterInput(colKey: string, value: string) {
    this.filters[colKey] = value;
    this.applyFilters();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  onDispositionChange(selectedValue: string, rowId: number) {
    const selected = this.lookupData.find(
      (d) => d.lookupValue === selectedValue
    );
    if (selected) {
      this.modifiedLookupData.push({
        leId: rowId || 0,
        lookupValue: selected.lookupValue.toString(),
      });
      if (!this.editCache[rowId]) {
        this.editCache[rowId] = {};
      }
      this.editCache[rowId].disposition = selected.lookupValue.toString();
    }
  }

  onEditRow(index: number) {
    const row = this.dataSource.filteredData[index];
    this.editingRows[row.letterEquipId] = true;
    this.editCache[row.letterEquipId] = { ...row };

    if (this.lookupData.length === 0) {
      this.bulkEditTechnicalLettersService.getLookUpData().subscribe({
        next: (data) => {
          this.lookupData = data;
        },
        error: (error) => {
          this.error = "Failed to load lookup data.";
          console.error("Error loading lookup data:", error);
        },
      });
    }
  }

  onDeleteRow(index: number) {
    const row = this.dataSource.filteredData[index];
    if (!row) {
      this.error = "Invalid row selected for deletion.";
      return;
    }

    if (confirm("Are you sure you want to delete this equipment?")) {
      this.bulkEditTechnicalLettersService
        .deleteBulkEditTechnicalLetter(row.letterEquipId)
        .subscribe({
          next: () => {
            const globalIndex = this.bulkEditData.findIndex(
              (r) => r.letterEquipId === row.letterEquipId
            );
            if (globalIndex > -1) {
              this.bulkEditData.splice(globalIndex, 1);
            }
            this.applyFilters();
          },
          error: () => {
            this.error = "Failed to delete equipment.";
          },
        });
    }
  }

  onSaveRow(index: number) {
    const row = this.dataSource.filteredData[index];
    const cache = this.editCache[row.letterEquipId];
    let selected: LookUpData | undefined;

    const payload: BulkEditTechnicalLetter = {
      ...row,
      ...cache,
    };

    if (payload.disposition) {
      selected = this.lookupData.find(
        (d) => d.lookupValue === payload.disposition
      );
      if (selected) {
        payload.disposition = selected.pid.toString();
      }
    }
    this.bulkEditTechnicalLettersService
      .updateBulkEditTechnicalLetter(payload)
      .subscribe({
        next: () => {
          if (selected) {
            payload.disposition = selected.lookupValue;
            row.disposition = selected.lookupValue;
          }
          Object.assign(row, payload);
          delete this.editingRows[row.letterEquipId];
          delete this.editCache[row.letterEquipId];
        },
        error: () => {
          this.error = "Failed to update equipment data";
        },
      });
  }

  onCancelEditRow(index: number) {
    const row = this.dataSource.filteredData[index];
    delete this.editingRows[row.letterEquipId];
    delete this.editCache[row.letterEquipId];
  }

  exportToExcel() {
    const headerRow = [
      "Letter Equip ID",
      "Issuer",
      "Letter Number",
      "Letter Title",
      "Recurring",
      "Issue Date",
      "Plant",
      "Equipment #",
      "Equipment Type",
      "Disposition",
      "Comments",
    ];
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Bulk Edit Data");
    worksheet.addRow(headerRow);
    worksheet.getRow(1).font = {
      bold: true,
      size: 12,
      color: { argb: "000000" },
    };
    worksheet.getRow(1).alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    const rows = this.bulkEditData.map((item) => [
      item.letterEquipId,
      item.letterIssuer,
      item.letterNumber,
      item.letterTitle,
      item.recurringFlag,
      new Date(item.letterIssueDate).toLocaleDateString(),
      item.plant,
      item.equipment,
      item.equipmentType,
      item.disposition,
      item.comments || "",
    ]);
    worksheet.addRows(rows);
    worksheet.eachRow((row, _) => {
      row.eachCell((cell, colNumber) => {
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: colNumber === 4,
        };
      });
    });

    worksheet.getColumn("A").width = 20; // Letter Equip ID
    worksheet.getColumn("B").width = 20; // Issuer
    worksheet.getColumn("C").width = 20; // Letter Number
    worksheet.getColumn("D").width = 40; // Letter Title
    worksheet.getColumn("E").width = 15; // Recurring
    worksheet.getColumn("F").width = 20; // Issue Date
    worksheet.getColumn("G").width = 20; // Plant
    worksheet.getColumn("H").width = 20; // Equipment #
    worksheet.getColumn("I").width = 20; // Equipment Type
    worksheet.getColumn("J").width = 20; // Disposition
    worksheet.getColumn("K").width = 30; // Comments

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Bulk_Edit.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    });
  }

  formatDate(input: string): string {
    const date = new Date(input);

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");

    return `${year}-${month}-${day}`;
  }
}
